import React from 'react';
import { IndustryTechStackItem, TechItem } from '../../data/industryData';
import '../../styles/TechStackSection.css';

interface TechStackSectionProps {
  title: string;
  description: string;
  items: IndustryTechStackItem[];
}

// Function to map icon string to image path
const getIconForTech = (iconName?: string) => {
  if (!iconName) return require('../../assets/Icons/default.png'); // Default icon
  
  // Map of icon identifiers to their corresponding image paths
  const iconMap: Record<string, string> = {
    // Cloud providers
    'aws': require('../../assets/Icons/aws.png'),
    'gcp': require('../../assets/Icons/gcp.png'),
    'azure': require('../../assets/Icons/azure.png'),
    'argocd': require('../../assets/Icons/argocd.png'),

    
    // Infrastructure
    'terraform': require('../../assets/Icons/terraform.png'),
    'terragrunt': require('../../assets/Icons/terragrunt.png'),
    'pulumi': require('../../assets/Icons/pulumi.png'),
    'atlantis': require('../../assets/Icons/atlantis.png'),
    'opentofu': require('../../assets/Icons/opentofu.png'),
    
    // Network
    'connect': require('../../assets/Icons/connect.png'),
    'link': require('../../assets/Icons/link.png'),
    'network': require('../../assets/Icons/network.png'),
    
    // Security
    'vault': require('../../assets/Icons/vault.png'),
    'opa': require('../../assets/Icons/opa.png'),
    'kyverno': require('../../assets/Icons/kyverno.png'),
    
    // // Monitoring
    'prometheus': require('../../assets/Icons/prometheus.png'),
    'grafana': require('../../assets/Icons/grafana.png'),
    'elk': require('../../assets/Icons/elk.png'),
    'opentelemetry': require('../../assets/Icons/opentelemetry.png'),
    
    // CI/CD & GitOps
    'github': require('../../assets/Icons/github.png'),
    'gitlab': require('../../assets/Icons/gitlab.png'),
    'argo': require('../../assets/Icons/argocd.png'),
    'helm': require('../../assets/Icons/helm.png'),
    'kustomize': require('../../assets/Icons/kustomize.png'),
    'jenkins': require('../../assets/Icons/jenkins.png'),
    'fluxcd': require('../../assets/Icons/fluxcd.png'),
    'hashicorp': require('../../assets/Icons/hashicorpvault.png'),
    'checkov': require('../../assets/Icons/checkov.png'),

    
    // Machine Learning
    'kubeflow': require('../../assets/Icons/kubeflow.png'),
    'mlflow': require('../../assets/Icons/mlflow.png'),
    'airflow': require('../../assets/Icons/airflow.png'),
    'seldon': require('../../assets/Icons/seldon.png'),
    'infracost': require('../../assets/Icons/infracost.png'),
    'kubecost': require('../../assets/Icons/kubecost.png'),
    'anthos':require('../../assets/Icons/anthos.png'),
    'openshift':require('../../assets/Icons/openshift.png'),
    'kubernetes':require('../../assets/Icons/kubernets.png'),
    'cloudformation':require('../../assets/Icons/cloudformation.png'),
    'externalsecrets':require('../../assets/Icons/externalsecret.png'),
    'kserve':require('../../assets/Icons/kserve.png'),
    'knative':require('../../assets/Icons/knative.png'),
    'tensorboard':require('../../assets/Icons/tensorboard.png'),
    'siem':require('../../assets/Icons/SIEM.png'),
    'iam':require('../../assets/Icons/iam.png'),
    'encryption':require('../../assets/Icons/encryption.png'),
    'etl':require('../../assets/Icons/etlPipelines.png'),
    'redis':require('../../assets/Icons/redis.png'),
    'cdn':require('../../assets/Icons/cdn.png'),
    'cart':require('../../assets/Icons/cartandcheckout.png'),
    'fraud':require('../../assets/Icons/frauddetection.png'),
    'identity':require('../../assets/Icons/frauddetection.png'),
    'data':require('../../assets/Icons/dataPrivacy.png'),
    'product':require('../../assets/Icons/catlog.png'),
    'pci':require('../../assets/Icons/security.png'),
    'a/b':require('../../assets/Icons/security.png'),
    'ehr':require('../../assets/Icons/security.png'),
    'anonymization':require('../../assets/Icons/frauddetection.png'),




    // Default
    'default': require('../../assets/Icons/default.png')
  };
  
  return iconMap[iconName.toLowerCase()] || iconMap.default;
};

// Title component with unique styling
const SectionTitle = ({ title }: { title: string }) => {
  // Split the title to highlight key words
  const words = title.split(' ');
  const highlightIndex = Math.floor(words.length / 2); // Highlight middle word
  
  return (
    <div className="title-container">
      <h2 className="tech-stack-title">
        {words.map((word, index) => 
          index === highlightIndex ? 
            <span key={index} className="title-highlight">{word} </span> : 
            <span key={index}>{word} </span>
        )}
      </h2>
      <span className="title-accent">
        <span className="dot"></span>
        <span className="dot"></span>
        <span className="dot"></span>
      </span>
    </div>
  );
};

const TechStackSection: React.FC<TechStackSectionProps> = ({ title, description, items }) => {
  return (
    <section className="tech-stack-section">
      <div className="glass-overlay"></div>
      <div className="tech-stack-container">
        <div className="tech-stack-header">
          <SectionTitle title={title} />
          <p className="tech-stack-description">{description}</p>
        </div>
        
        <div className="tech-stack-categories">
          {items.map((item, index) => (
            <div className="tech-stack-category-block" key={index}>
              <div className="category-line">
                <span className="category-number">{index < 9 ? `0${index + 1}` : index + 1}</span>
                <h3 className="category-title">{item.title}</h3>
                <div className="category-line-fill"></div>
              </div>
              
              <div className="tech-list">
                {item.technologies.map((tech, i) => {
                  const isObject = typeof tech !== 'string';
                  const techName = isObject ? (tech as TechItem).name : tech;
                  const techIcon = isObject ? (tech as TechItem).icon : undefined;
                  
                  // Get the icon path
                  const iconPath = getIconForTech(techIcon || techName.toLowerCase().split(' ')[0]);
                  
                  return (
                    <div className="tech-item" key={i}>
                      <span className="industry-tech-icon">
                        <img 
                          src={iconPath} 
                          alt={techName} 
                          className="industry-tech-icon-image"
                        />
                      </span>
                      <span className="tech-name">{techName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="geometric-shape shape-1"></div>
      <div className="geometric-shape shape-2"></div>
      <div className="geometric-shape shape-3"></div>
      <div className="geometric-shape shape-4"></div>
    </section>
  );
};

export default TechStackSection;